/**
 * editor2/modals/PaymentEmailModal.tsx
 * 支払い確認メール送信モーダル
 * Step 1: 言語選択 (ja/en/tw/kr)
 * Step 2: プレビュー表示 (to/subject/body) → 送信
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import type { EditorReservation } from '../types';

export interface PaymentEmailState {
  open: boolean;
  step: 'lang' | 'ai_compose' | 'preview';
  type: string; // 'payment' | 'free' | 'request_ok' | 'request_full' | 'request_offer' | 'cancel' | 'cancel_fee' | 'comment' | 'ai_compose'
  lang: string; // 'ja' | 'en' | 'tw' | 'kr' — AI改善時に必要
  to: string;
  subject: string;
  body: string;
  sending: boolean;
  error: string;
  aiInstruction: string; // 改善指示（プレビュー画面のAI改善で使う）
  aiImproving: boolean;
  composePrompt: string; // 生成指示（ai_compose ステップで使う、AIで作成）
  aiComposing: boolean;
}

export const INITIAL_PAYMENT_EMAIL_STATE: PaymentEmailState = {
  open: false,
  step: 'lang',
  type: 'payment',
  lang: 'ja',
  to: '',
  subject: '',
  body: '',
  sending: false,
  error: '',
  aiInstruction: '',
  aiImproving: false,
  composePrompt: '',
  aiComposing: false,
};

interface PaymentEmailModalProps {
  open: boolean;
  editing: EditorReservation;
  sysApiBase: string;
  onClose: () => void;
  onSend: (emailData: { to: string; subject: string; body: string }) => void;
  // State management delegated to parent/hook
  state: PaymentEmailState;
  onStateChange: (updater: (prev: PaymentEmailState) => PaymentEmailState) => void;
  onLangSelect: (lang: string) => void;
  onAiImprove: () => void; // 改善指示 + 現在の本文を Gemini に送り、結果で本文を置換
  onAiCompose: () => void; // composePrompt + 予約データ + 基本ルールを Gemini に送り、メール本文を新規生成
}

function getEmailTypeTitle(type: string, step: string): string {
  if (step === 'preview') return 'メールプレビュー';
  if (step === 'ai_compose') return '✨ AIでメール本文を作成';
  switch (type) {
    case 'payment':
      return '支払い確認メールを作成しますか？';
    case 'request_ok':
      return 'リクエスト承認メールを作成しますか？';
    case 'request_full':
      return 'リクエスト満車回答メールを作成しますか？';
    case 'request_offer':
      return 'リクエスト代替案メールを作成しますか？';
    case 'cancel':
      return 'キャンセル連絡メールを作成しますか？';
    case 'cancel_fee':
      return 'キャンセル料連絡メールを作成しますか？';
    case 'comment':
      return 'コメント返信メールを作成しますか？';
    default:
      return 'フリーメールを作成しますか？';
  }
}

export default function PaymentEmailModal({
  open,
  state,
  onClose,
  onSend,
  onStateChange,
  onLangSelect,
  onAiImprove,
  onAiCompose,
}: PaymentEmailModalProps) {
  const title = getEmailTypeTitle(state.type, state.step);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers>
        {state.step === 'lang' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('ja')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              日本語
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('en')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              英語
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('tw')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              中国語（繁体字）
            </Button>
            <Button
              variant="outlined"
              onClick={() => onLangSelect('kr')}
              sx={{ py: 1.5, justifyContent: 'flex-start' }}
            >
              韓国語
            </Button>
          </Box>
        ) : state.step === 'ai_compose' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              どんなメールを作りますか？ 言語を指定したい場合は「英文で」「韓国語で」など指示文に含めてください。
              予約データ（顧客名・予約コード・日時・料金等）と基本ルール（キャンセルポリシー等）は自動で AI に渡されます。
            </Typography>
            <TextField
              label="生成指示"
              fullWidth
              multiline
              rows={6}
              autoFocus
              placeholder={'例)\n・ 直前のキャンセルに対するお詫びと、キャンセル料の案内を英文で\n・ 来店時間変更の確認を簡潔に\n・ 雨天の予報があるので、車両受け取り時の注意喚起を一言'}
              value={state.composePrompt}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, composePrompt: e.target.value }))
              }
              disabled={state.aiComposing}
            />
            {state.error && (
              <Typography variant="caption" color="error">
                {state.error}
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="宛先"
              fullWidth
              size="small"
              value={state.to}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, to: e.target.value }))
              }
            />
            <TextField
              label="件名"
              fullWidth
              size="small"
              value={state.subject}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, subject: e.target.value }))
              }
            />
            <TextField
              label="本文"
              fullWidth
              multiline
              rows={10}
              value={state.body}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, body: e.target.value }))
              }
            />
            {state.error && (
              <Typography variant="caption" color="error">
                {state.error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Button onClick={onClose} color="inherit">
          いいえ
        </Button>
        {state.step === 'ai_compose' && (
          <Button
            onClick={onAiCompose}
            variant="contained"
            disabled={state.aiComposing || !state.composePrompt.trim()}
            startIcon={
              state.aiComposing ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              whiteSpace: 'nowrap',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
              },
            }}
          >
            {state.aiComposing ? '生成中…' : '✨ 生成する'}
          </Button>
        )}
        {state.step === 'preview' && (
          <>
            <TextField
              size="small"
              multiline
              rows={2}
              placeholder="改善指示（任意）: もっと短く、敬語強めて、英文に直して…"
              value={state.aiInstruction}
              onChange={(e) =>
                onStateChange((prev) => ({ ...prev, aiInstruction: e.target.value }))
              }
              disabled={state.aiImproving || state.sending}
              sx={{ flex: 1, minWidth: 200 }}
            />
            <Button
              onClick={onAiImprove}
              variant="outlined"
              disabled={state.aiImproving || state.sending || !state.body.trim()}
              startIcon={
                state.aiImproving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{ whiteSpace: 'nowrap' }}
            >
              {state.aiImproving ? '改善中…' : '✨ AI改善'}
            </Button>
            <Button
              onClick={() => onSend({ to: state.to, subject: state.subject, body: state.body })}
              variant="contained"
              disabled={state.sending || state.aiImproving}
              startIcon={
                state.sending ? <CircularProgress size={20} color="inherit" /> : undefined
              }
            >
              送信する
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
